import { useEffect } from 'react'
import { useStoryblokState } from '@cinch-nx/data-storyblok'
import { Head, renderWithResolver } from '@cinch-nx/landing-ui-shared'
import { Wrapper } from '@cinch-nx/shared-ui'
import { EvBenefits } from './components/ev-benefits'
import { EvCharging } from './components/ev-charging'
import { EvFaqs } from './components/ev-faqs'
import { EvInfoCard } from './components/ev-info-card'
import { EvPreFooter } from './components/ev-pre-footer'
import { EvRange } from './components/ev-range'
import { ExploreEvs } from './components/explore-our-evs'
import { EvIntro } from './components/intro/intro'
import { JargonBuster } from './components/jargon-buster'
import { ElectricCarsProps } from './get-static-props'
import styles from './landing-feature-electric-cars.module.css'
import cx from 'classnames'
import { CompareBar, CompareBarProvider } from '@cinch-labs/shared-compare-cars'
import { useUserStore } from '@cinch-nx/data-user'
import { useFavouritesStore } from '@cinch-nx/data-favourites'
import { handlePostAuthFavourite } from '@cinch-labs/shared-auth-modal'

export function FeatureElectricCars({
  story,
  generatedAt,
  preview,
}: ElectricCarsProps) {
  const { content } = useStoryblokState({ story, enableBridge: preview })
  const { status: userStatus } = useUserStore()
  const { addFavourite } = useFavouritesStore()

  const {
    intro,
    benefitsOfAnElectricCar,
    chargingAnElectricCar,
    electricCarRange,
    didYouKnow,
    electricCarsJargon,
    electricCarsFaqs,
    preFooter,
  } = content
  const evChargingContent = chargingAnElectricCar[0]
  const evRangeContent = electricCarRange[0]
  const evJargonContent = electricCarsJargon[0]
  const { title: faqTitle, faqs } = electricCarsFaqs[0]
  const { evCta, image } = preFooter[0]

  const SEO = content?.SEO?.[0]

  const contentIntroText = renderWithResolver(intro)

  const isLoggedIn = userStatus === 'valid'

  useEffect(() => {
    handlePostAuthFavourite(addFavourite, isLoggedIn)
  }, [addFavourite, isLoggedIn])

  return (
    <CompareBarProvider>
      <Head
        {...SEO}
        generatedAt={generatedAt}
        fallbackTitle="Electric cars - cinch"
        fallbackDescription="Plug into the benefits of making your drive more electric, and choose from a wide range of electric cars to buy entirely online."
      />
      <article className={styles.root}>
        <Wrapper className={{ container: styles.electricCarsWrapper }} isSlim>
          <EvIntro introText={contentIntroText} />
        </Wrapper>
        <Wrapper className={{ container: styles.electricCarsWrapper }} isSlim>
          <EvBenefits evBenefits={benefitsOfAnElectricCar} />
        </Wrapper>
        <Wrapper
          className={{
            container: cx(styles.electricCarsWrapper, styles.sectionSpacer),
          }}
          isSlim
        >
          <ExploreEvs />
        </Wrapper>
        <Wrapper
          className={{
            container: cx(styles.electricCarsWrapper, styles.sectionSpacer),
          }}
          isSlim
        >
          <EvCharging evCharging={evChargingContent} />
        </Wrapper>
        <Wrapper className={{ container: styles.electricCarsWrapper }} isSlim>
          <EvRange evRange={evRangeContent} />
        </Wrapper>
        <Wrapper className={{ container: styles.electricCarsWrapper }} isSlim>
          <EvInfoCard evInfo={didYouKnow} />
        </Wrapper>
        <Wrapper className={{ container: styles.electricCarsWrapper }} isSlim>
          <EvFaqs title={faqTitle} faqs={faqs} />
        </Wrapper>
        <JargonBuster jargonContent={evJargonContent} />
        <EvPreFooter evCta={evCta} image={image} />
        <CompareBar />
      </article>
    </CompareBarProvider>
  )
}

export default FeatureElectricCars
